/**
 * Card Component
 * Reusable card component with header, content, and footer sections
 */

import React, { ReactNode } from 'react';
import { clsx } from 'clsx';

export interface CardProps {
  children: ReactNode;
  className?: string;
  padding?: 'none' | 'sm' | 'md' | 'lg';
  shadow?: 'none' | 'sm' | 'md' | 'lg';
  border?: boolean;
  rounded?: 'none' | 'sm' | 'md' | 'lg' | 'xl';
}

export interface CardHeaderProps {
  children: ReactNode;
  className?: string;
  border?: boolean;
}

export interface CardContentProps {
  children: ReactNode;
  className?: string;
}

export interface CardFooterProps {
  children: ReactNode;
  className?: string;
  border?: boolean;
}

const CardRoot: React.FC<CardProps> = ({
  children,
  className,
  padding = 'md',
  shadow = 'md',
  border = false,
  rounded = 'lg',
}) => {
  const paddingClasses = {
    none: '',
    sm: 'p-3',
    md: 'p-6',
    lg: 'p-8',
  };

  const shadowClasses = {
    none: '',
    sm: 'shadow-sm',
    md: 'shadow',
    lg: 'shadow-lg',
  };

  const roundedClasses = {
    none: '',
    sm: 'rounded-sm',
    md: 'rounded-md',
    lg: 'rounded-lg',
    xl: 'rounded-xl',
  };

  const cardClasses = clsx(
    'bg-white',
    paddingClasses[padding],
    shadowClasses[shadow],
    roundedClasses[rounded],
    border && 'border border-gray-200',
    className
  );

  return <div className={cardClasses}>{children}</div>;
};

const CardHeader: React.FC<CardHeaderProps> = ({
  children,
  className,
  border = true,
}) => {
  const headerClasses = clsx(
    'pb-4',
    border && 'border-b border-gray-200 mb-4',
    className
  );

  return <div className={headerClasses}>{children}</div>;
};

const CardContent: React.FC<CardContentProps> = ({ children, className }) => {
  return <div className={className}>{children}</div>;
};

const CardFooter: React.FC<CardFooterProps> = ({
  children,
  className,
  border = true,
}) => {
  const footerClasses = clsx(
    'pt-4',
    border && 'border-t border-gray-200 mt-4',
    className
  );

  return <div className={footerClasses}>{children}</div>;
};

// Create the compound component
const Card = Object.assign(CardRoot, {
  Header: CardHeader,
  Content: CardContent,
  Footer: CardFooter,
});

export { Card };
